import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Row, Column, SelectBox } from '@lux/components';
import { Text, theme } from '@dls/web';
import { noop } from '@lux/helpers';

import { H4 } from '../Base';
import useTranslate from '../../hooks/useTranslation';

const StyledColumn = styled(props => <Column {...props} />)`
  padding: ${p => p.theme.spacing(1)};
`;

const SizeSelection = styled(SelectBox)`
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: none;
  height: 4rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 0 0 1px ${p => p.theme.colours.grey_400};

  &:hover {
    border: none;

    ${p =>
      !p.disabled &&
      !p.active &&
      css`
        box-shadow: 0 0 0 1px ${p.theme.colours.grey_600};
      `};
  }

  ${p =>
    p.active &&
    css`
      box-shadow: 0 0 0 2px ${theme.main.colours.secondary.d1};
    `};

  ${p =>
    p.disabled &&
    css`
      ${H4} {
        color: ${p.theme.colours.grey_500};
      }
      background: ${p.theme.colours.grey_200};
      border: none;
      box-shadow: none;
    `};
`;

const StyledRow = styled(props => <Row {...props} />)`
  margin-bottom: ${p => p.theme.spacing(1)};

  ${p => p.theme.media.md`
    margin-bottom: ${p.theme.spacing(2)};
  `};
`;

const PhoneSizeSelection = ({
  errorMessage,
  skuMessage,
  selectedSize,
  sizes,
  onChange
}) => {
  const isOutOfStock = sizes.every(size => size.available === false);
  const { t } = useTranslate();

  useEffect(() => {
    if (!selectedSize && !isOutOfStock) {
      // Select the first availble item in the sizes as the default selection
      const defaultSize = sizes.filter(size => size.available)[0];
      onChange(defaultSize);
    }
  }, [isOutOfStock, selectedSize, sizes, onChange]);

  // Get valid sizes with prices defined
  const sizesWithPrices = sizes.filter(size => size.price !== undefined);

  const renderSizes = [];
  const map = new Map();

  for (const item of sizesWithPrices) {
    if (!map.has(item.name)) {
      map.set(item.name, true);
      renderSizes.push(item);
    }
  }

  return (
    <Fragment>
      {sizesWithPrices.length > 0 && (
        <Text type="header">Choose your size</Text>
      )}
      <StyledRow>
        {renderSizes.map((size, key) => {
          const { active, available, name, price } = size;

          return (
            <StyledColumn key={key} sm={6} lg={4}>
              <SizeSelection
                disabled={!available || price === undefined}
                active={active}
                onClick={() => available && onChange(size)}
              >
                <Text type="body" data-testid={name}>
                  {name.trim()}
                </Text>
              </SizeSelection>
              {!errorMessage && (!available || price === undefined) && (
                <Text type="smallBody">{t('OUT_OF_STOCK')}</Text>
              )}
              {available && active && skuMessage && (
                <Text type="smallBody">{skuMessage}</Text>
              )}
            </StyledColumn>
          );
        })}
      </StyledRow>
    </Fragment>
  );
};

PhoneSizeSelection.defaultProps = {
  selectedSize: '',
  sizes: [],
  onChange: noop,
  disabled: false
};

PhoneSizeSelection.propTypes = {
  /** The selected size of the phone */
  selectedSize: PropTypes.string,
  /** List of sizes to render */
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      available: PropTypes.bool,
      active: PropTypes.bool,
      price: PropTypes.number
    })
  ),
  /** onChange event handler, returns full size object */
  onChange: PropTypes.func,
  /** Disable seize */
  disabled: PropTypes.bool
};

export default PhoneSizeSelection;
